// resources/js/admin/components/shared/pages/MediaAttachment.tsx

import { useState, useEffect } from "react";
import { Upload, X, Image as ImageIcon, RefreshCw, File, Video } from "lucide-react";
import { Media } from "../../../types/features/Media";
import { mediaService } from "../../../services/mediaService";
import { pageMediaService } from "../../../services/pageMediaService";
import useToken from "../../../hooks/features/auth/useToken";
import useMedia from "../../../hooks/features/media/useMedia";

interface MediaAttachmentProps {
    siteSlug: string;
    pageId?: string;
    initialMedia?: Record<string, string> | Media[] | null;
    onMediaChange: (media: Array<{ id: string; field: string; url: string }>) => void;
}

export default function MediaAttachment({
    siteSlug,
    pageId,
    initialMedia,
    onMediaChange,
}: MediaAttachmentProps) {
    const { token } = useToken();
    const { media: availableMedia, fetchMedia } = useMedia(siteSlug);
    const [attachedMedia, setAttachedMedia] = useState<Array<{ id: string; field: string; url: string }>>([]);

    // Upload
    const [mediaName, setMediaName] = useState("");
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [mediaPreview, setMediaPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    // Sélection existant
    const [selectedMediaId, setSelectedMediaId] = useState("");
    const [selectedFieldName, setSelectedFieldName] = useState("");

    // Initialiser les médias attachés
    useEffect(() => {
        if (initialMedia) {
            if (Array.isArray(initialMedia)) {
                const existingMedia = initialMedia
                    .filter((item: any) => item.pivot?.field)
                    .map((item: any) => ({
                        id: item.id || '',
                        field: item.pivot.field,
                        url: item.url || '',
                    }));
                setAttachedMedia(existingMedia);
                onMediaChange(existingMedia);
            } else if (typeof initialMedia === 'object' && !Array.isArray(initialMedia)) {
                const existingMedia = Object.entries(initialMedia)
                    .filter(([field, url]) => field && url)
                    .map(([field, url]) => ({
                        id: '',
                        field,
                        url: typeof url === 'string' ? url : '',
                    }));
                setAttachedMedia(existingMedia);
                onMediaChange(existingMedia);
            }
        }
    }, [initialMedia]);

    const selectExistingMedia = () => {
        if (!selectedMediaId || !selectedFieldName.trim()) {
            alert("Veuillez sélectionner un média et saisir un nom de champ");
            return;
        }

        const media = availableMedia?.find((m: Media) => m.id === selectedMediaId);
        if (media) {
            const field = selectedFieldName.trim().toLowerCase().replace(/\s+/g, '-');
            const exists = attachedMedia.some(m => m.field === field);
            if (exists) {
                alert(`Le champ "${field}" est déjà utilisé`);
                return;
            }

            const newMedia = [...attachedMedia, {
                id: media.id,
                field: field,
                url: media.url,
            }];
            setAttachedMedia(newMedia);
            onMediaChange(newMedia);

            setSelectedMediaId("");
            setSelectedFieldName("");
        }
    };

    const removeAttachedMedia = async (field: string) => {
        const mediaToRemove = attachedMedia.find(m => m.field === field);
        if (!mediaToRemove) return;

        console.log(pageId , mediaToRemove.id)

        // Si on a un pageId et un mediaId, appeler l'API pour détacher
        if (pageId && mediaToRemove.id) {
            try {
                await pageMediaService.detach(token, pageId, mediaToRemove.id);
                console.log("succes")
            } catch (error) {
                console.error("Erreur lors du détachement", error);
                alert("Erreur lors du détachement du média");
                return;
            }
        }

        // Supprimer du state local
        const newMedia = attachedMedia.filter(m => m.field !== field);
        setAttachedMedia(newMedia);
        onMediaChange(newMedia);
    };

    const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setMediaFile(file);
            const previewUrl = URL.createObjectURL(file);
            setMediaPreview(previewUrl);
        }
        e.target.value = "";
    };

    const handleMediaUpload = async () => {
        if (!mediaFile || !mediaName.trim()) {
            alert("Veuillez saisir un nom et sélectionner un fichier");
            return;
        }

        setUploading(true);
        try {
            const response = await mediaService.upload(token, {
                site_slug: siteSlug,
                name: mediaName.trim(),
                file: mediaFile,
                alt_text: mediaName.trim(),
                is_public: true,
            });

            const newMedia = response;
            await fetchMedia();

            const field = mediaName.trim().toLowerCase().replace(/\s+/g, '-');
            const updatedMedia = [...attachedMedia, {
                id: newMedia.id,
                field: field,
                url: newMedia.url,
            }];
            setAttachedMedia(updatedMedia);
            onMediaChange(updatedMedia);

            setMediaName("");
            setMediaFile(null);
            setMediaPreview(null);

            alert("Média uploadé et attaché avec succès");
        } catch (error) {
            console.error("Erreur lors de l'upload", error);
            alert("Erreur lors de l'upload du média");
        } finally {
            setUploading(false);
        }
    };

    const getFileIcon = (url: string) => {
        if (!url) return File;
        if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)) return ImageIcon;
        if (url.match(/\.(mp4|webm|ogg|mov|avi)$/i)) return Video;
        return File;
    };

    const isImageUrl = (url: string) => {
        if (!url) return false;
        return url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) !== null;
    };

    return (
        <div className="mb-6 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Médias attachés</h3>
                <button
                    type="button"
                    onClick={() => {
                        fetchMedia();
                    }}
                    className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                >
                    <RefreshCw size={14} />
                    Rafraîchir
                </button>
            </div>

            {attachedMedia.length > 0 && (
                <div className="mb-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {attachedMedia.map((media) => {
                        const Icon = getFileIcon(media.url);
                        return (
                            <div key={media.field} className="relative group bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                    {isImageUrl(media.url) ? (
                                        <img
                                            src={media.url}
                                            alt={media.field}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Icon className="w-12 h-12 text-gray-400" />
                                    )}
                                </div>

                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                                    <span className="text-white text-xs font-medium text-center break-all mb-2">
                                        {media.field}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachedMedia(media.field)}
                                        className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] font-medium px-2 py-0.5 rounded truncate max-w-[calc(100%-8px)]">
                                    {media.field}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Choix 1: Sélectionner un média existant */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sélectionner un média existant
                </label>
                <div className="flex gap-2">
                    <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                        onChange={(e) => setSelectedMediaId(e.target.value)}
                        value={selectedMediaId}
                    >
                        <option value="">Choisir un média...</option>
                        {availableMedia
                            ?.filter((m: Media) => m.mime_type.startsWith('image/'))
                            .map((media: Media) => (
                                <option key={media.id} value={media.id}>
                                    {media.slug} ({media.filename})
                                </option>
                            ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Nom du champ (ex: hero_image)"
                        className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                        value={selectedFieldName}
                        onChange={(e) => setSelectedFieldName(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={selectExistingMedia}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                    >
                        Attacher
                    </button>
                </div>
            </div>

            {/* Choix 2: Uploader un nouveau média */}
            <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Uploader un nouveau média</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom du média (sera utilisé comme clé)
                        </label>
                        <input
                            type="text"
                            value={mediaName}
                            onChange={(e) => setMediaName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                            placeholder="hero-background"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fichier
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleMediaFileChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 text-sm"
                            />
                            {mediaPreview && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMediaPreview(null);
                                        setMediaFile(null);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                        {mediaPreview && (
                            <div className="mt-2">
                                <img src={mediaPreview} alt="Aperçu" className="w-20 h-20 object-cover rounded-lg" />
                            </div>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleMediaUpload}
                    disabled={uploading || !mediaFile || !mediaName.trim()}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                    <Upload size={18} />
                    {uploading ? "Upload en cours..." : "Uploader et attacher"}
                </button>
            </div>
        </div>
    );
}